import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetSecurityGroupsQuery, useDeleteSecurityGroupMutation } from '../../services/portal';
import DataGrid from '../../components/Users/DataGrid';
import DeleteGroupModal from './DeleteGroupModal';
import LoadingState from './LoadingState';
import ErrorState from './ErrorState';

/**
 * Lists security groups with actions to create, edit, and delete.
 * Handles loading and error states and coordinates the delete confirmation modal.
 * - Fetches groups via RTK Query and renders a DataGrid.
 * - Opens a confirmation modal before deleting a group.
 * - Navigates to create/edit pages.
 * @returns {JSX.Element} Security Groups page with grid and actions.
 */
const SecurityGroups = () => {
  const navigate = useNavigate();

  const { data: securityGroups, isLoading, error, refetch: refetchSecurityGroups } = useGetSecurityGroupsQuery();
  const [deleteSecurityGroup] = useDeleteSecurityGroupMutation();

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [deleteModalShow, setDeleteModalShow] = useState(false);
  /**
   * Navigates to the "Create Security Group" page.
   * @returns {void}
   */
  const handleNewSecurityGroup = () => navigate('/security-groups/new');

  /**
   * Navigates to the "Edit Security Group" page for the provided group.
   * @param {{ SecurityGroupID: number }} group - The selected group row from the grid.
   * @returns {void}
   */

  const handleEditSecurityGroup = (group) => navigate(`/security-groups/${group.SecurityGroupID}`);

  /**
   * Opens the delete confirmation modal for a given group.
   * @param {Object} group - The group object to delete.
   * @returns {void}
   */
  const handleDeleteModalShow = (group) => {
    setSelectedGroup(group);
    setDeleteModalShow(true);
  };

  /**
   * Hides the delete confirmation modal and clears the selected group.
   * @returns {void}
   */
  const handleDeleteModalHide = () => {
    setDeleteModalShow(false);
    setSelectedGroup(null);
  };

  /**
   * Deletes a security group by ID and refreshes the groups list.
   * Intended to be passed to DeleteGroupModal's onConfirm.
   * @param {number} groupId - The ID of the group to delete.
   * @returns {Promise<void>}
   */
  const handleDeleteSecurityGroup = async (groupId) => {
    await deleteSecurityGroup(groupId).unwrap();
    refetchSecurityGroups();
  };


  const columns = [
    { field: 'GroupName', header: 'Group Name' },
    { field: 'GroupDescription', header: 'Description' },
    { field: 'OrganizationName', header: 'Organization' },
    { field: 'UserCount', header: 'Users' },
    { field: 'ReportCount', header: 'Reports' }
  ];

  const rows = securityGroups?.security_groups ?? [];

  if (isLoading) {
    return <LoadingState message="Loading security groups..." />;
  }

  if (error) {
    return (
      <ErrorState
        message="Error Loading Security Groups"
        onRetry={() => refetchSecurityGroups()}
      />
    );
  }

  return (
    <div className="page-inner">
      <header className="page-title-bar">
        <div className="d-flex justify-content-between">
          <h1 className="page-title">
            <i className="fas fa-shield-alt text-muted mr-2"></i>Security Groups
          </h1>
          <div className="btn-toolbar">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleNewSecurityGroup}>
              + New Group
            </button>
          </div>
        </div>
      </header>

      <div className="page-section">
        <div className="card">
          <div className="card-body">
            <DataGrid
              data={rows}
              columns={columns}
              onEditRow={handleEditSecurityGroup}
              onDeleteRow={handleDeleteModalShow}
              showDelete={true}
              showEditRow={true}
            />
          </div>
        </div>
      </div>

      <DeleteGroupModal
        show={deleteModalShow}
        onHide={handleDeleteModalHide}
        group={selectedGroup}
        onConfirm={handleDeleteSecurityGroup}
      />
    </div>
  );
};

export default SecurityGroups;
